import React, { forwardRef, useEffect, useRef, useState } from "react";
import Input from "./Input";
import IconButton from "./IconButton";
import Icon from "./Icon";
import { getBaseCurrency, symbolFor } from "../../core/currency";
import { formatNumber as fmt, parseNumber } from "./numberFormat";
import "./NumberInput.scss";

// Fontaine M20 — NumberInput: numbers and money typed by people. Replaces react-currency-input-field.
// The VALUE is a number (major units) or null; the TEXT is what the person sees: grouped with
// en-GB separators on blur, free while typing. `currency` adds the symbol as a prefix (V2 input-money).

/**
 * @param {number|null} value
 * @param {(value: number|null) => void} onChange
 * @param {string} [currency]                 ISO code → symbol prefix (defaults to base when `money`)
 * @param {boolean} [money]                   money mode: base currency symbol, 2 decimals on blur (0 if whole)
 * @param {number} [min] / [max] / [step]     clamp on blur; step for the +/− buttons and arrow keys
 * @param {number} [decimals]                 fraction digits on blur
 * @param {boolean} [stepper]                 show − / + buttons
 * @param {string} [suffix]                   unit ("mm", "%")
 */
const NumberInput = forwardRef(function NumberInput(
  { value, onChange, currency, money = false, min, max, step = 1, decimals, stepper = false, suffix, disabled = false, size = "lg", placeholder = "0", "aria-label": ariaLabel, label, onFocus, onBlur, onKeyDown, ...rest },
  ref
) {
  const cur = money ? currency || getBaseCurrency() : currency;
  const dec = decimals ?? (money ? (Number.isInteger(value ?? 0) ? 0 : 2) : undefined);
  const [text, setText] = useState(fmt(value, dec));
  const [focused, setFocused] = useState(false);

  // Re-sync the text only when the VALUE prop actually changes (not on every blur), so a parent that
  // has not re-rendered yet never wipes what was just typed.
  const prevValue = useRef(value);
  useEffect(() => {
    if (prevValue.current !== value) {
      prevValue.current = value;
      if (!focused) setText(fmt(value, dec));
    }
  }, [value, dec, focused]);

  const clamp = (n) => {
    if (n == null) return null;
    let v = n;
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    return v;
  };
  const commit = (n) => {
    const v = clamp(n);
    onChange?.(v);
    setText(fmt(v, dec));
  };
  const nudge = (dir) => {
    if (disabled) return;
    const base = value ?? (parseNumber(text) ?? 0);
    const next = Math.round((base + dir * step) * 1e6) / 1e6;
    commit(next);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      size={size}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel || label}
      className="ft-number"
      {...rest}
      prefix={cur ? <span className="ft-number__currency">{symbolFor(cur)}</span> : undefined}
      suffix={
        stepper ? (
          <span className="ft-number__stepper">
            <IconButton label="Decrease" size="sm" variant="ghost" disabled={disabled || (min != null && (value ?? 0) <= min)} onClick={() => nudge(-1)} tabIndex={-1}><Icon name="Minus" size="xs" /></IconButton>
            <IconButton label="Increase" size="sm" variant="ghost" disabled={disabled || (max != null && (value ?? 0) >= max)} onClick={() => nudge(1)} tabIndex={-1}><Icon name="Plus" size="xs" /></IconButton>
          </span>
        ) : suffix ? (
          suffix
        ) : undefined
      }
      onFocus={(e) => { setFocused(true); onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); commit(parseNumber(text)); onBlur?.(e); }}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        const n = parseNumber(t);
        if (n !== value) onChange?.(n);
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp") { e.preventDefault(); nudge(1); }
        else if (e.key === "ArrowDown") { e.preventDefault(); nudge(-1); }
        onKeyDown?.(e);
      }}
    />
  );
});

export default NumberInput;
